import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import config from 'config'

import { threeStars as ThreeStars } from 'assets/icons/groove/v2'

import { selectHasHelplyCopilotAddon } from 'ducks/accounts/selectors/selectHasHelplyCopilotAddon'
import { selectAccountSubdomain } from 'ducks/accounts/selectors/selectAccountSubdomain'
import { selectCurrentTicketId } from 'ducks/tickets/selectors/selectCurrentTicketId'
import { selectCurrentConversationById } from 'ducks/tickets/selectors'
import { selectConversationEventGroupsByConversationId } from 'ducks/tickets/selectors/selectConversationEventGroupsByConversationId'
import { selectTicketRequestByConversationId } from 'ducks/tickets/selectors/selectTicketRequestByConversationId'
import { selectCurrentUser } from 'ducks/currentUser/selectors/selectCurrentUser'
import { selectCurrentContact } from 'ducks/crm/contacts/selectors/current'
import { selectDraftByTicketId } from 'ducks/drafts2/selectors'
import { selectIsInNoteMode } from 'selectors/location'
import { selectIsRightSidebarCollapsed } from 'selectors/app/selectIsRightSidebarCollapsed'

import { doToggleRightSidebar } from 'actions/app'

import {
  doInsertHelplyCopilotText,
  doFetchHelplyCopilotGrant,
  doSetHelplyCopilotEditor,
} from './operations'
import { styles } from './styles'

function getHelplyOrigin() {
  try {
    return new URL(config.helplyCopilotUrl).origin
  } catch (e) {
    return null
  }
}

export default function HelplyCopilotPanel() {
  const dispatch = useDispatch()
  const hasCopilotAddon = useSelector(selectHasHelplyCopilotAddon)
  const subdomain = useSelector(selectAccountSubdomain)
  const ticketId = useSelector(selectCurrentTicketId)
  const conversation = useSelector(
    state => (ticketId ? selectCurrentConversationById(state, ticketId) : null)
  )
  const eventGroups = useSelector(
    state =>
      ticketId
        ? selectConversationEventGroupsByConversationId(state, ticketId)
        : null
  )

  const isTicketLoaded = useSelector(
    state =>
      ticketId
        ? selectTicketRequestByConversationId(state, ticketId).loaded
        : false
  )
  const currentUser = useSelector(selectCurrentUser)
  const contact = useSelector(selectCurrentContact)
  const isNoteMode = useSelector(selectIsInNoteMode)
  const isRightSidebarCollapsed = useSelector(selectIsRightSidebarCollapsed)
  const replyDraft = useSelector(
    state => (ticketId ? selectDraftByTicketId(state, ticketId, 'reply') : null)
  )
  const noteDraft = useSelector(
    state => (ticketId ? selectDraftByTicketId(state, ticketId, 'note') : null)
  )
  // Mirror whichever composer is active so Format reflects what the agent is
  // editing (reply or note) — otherwise Format stays disabled while writing a note.
  const activeDraft = isNoteMode ? noteDraft : replyDraft

  const [isOpen, setIsOpen] = useState(false)
  // Once opened, keep the iframe mounted (just hidden by the collapsed drawer)
  // so it doesn't re-authenticate and flash the loading skeleton on every
  // reopen — Zendesk's sidebar iframe is likewise persistent.
  const [hasOpened, setHasOpened] = useState(false)
  const iframeRef = useRef(null)
  // Tracks the previous open state so we can detect reopen transitions.
  const wasOpenRef = useRef(false)
  // Mirror the right-sidebar collapse state so the open effect reads it at the
  // moment the copilot opens, without re-running (and re-opening) if the agent
  // later collapses it themselves.
  const isRightSidebarCollapsedRef = useRef(isRightSidebarCollapsed)
  isRightSidebarCollapsedRef.current = isRightSidebarCollapsed
  const helplyOrigin = useMemo(getHelplyOrigin, [])

  const agentName = useMemo(
    () => {
      if (!currentUser) return null
      const full = [currentUser.first_name, currentUser.last_name]
        .filter(Boolean)
        .join(' ')
        .trim()
      return currentUser.name || full || currentUser.email || null
    },
    [currentUser]
  )

  const ticketContext = useMemo(
    () => {
      if (!ticketId) return null
      const comments = (eventGroups || [])
        .map(group => {
          // `group.summary` is the (possibly summary-only) message. The full
          // HTML body is present once a group is fully loaded; groups loaded as
          // summaries carry only the plain text on `bodyPlainText`, so fall back
          // to it — otherwise the comment is filtered out as empty below and the
          // AI loses that slice of the conversation.
          const body =
            (group && group.summary && group.summary.body) ||
            (group && group.summary && group.summary.bodyPlainText) ||
            (group && group.body) ||
            ''
          // The message author lives on `summary.author` (a Contact/Agent with
          // a real name); `actor` is the secondary fallback for system events.
          const authorName =
            (group &&
              group.summary &&
              group.summary.author &&
              group.summary.author.name) ||
            (group && group.actor && group.actor.name) ||
            'Unknown'
          const createdAt =
            (group && group.summary && group.summary.createdAt) ||
            (group && group.createdAt) ||
            new Date().toISOString()
          return {
            author: { name: authorName },
            value: body,
            created_at: createdAt,
            public: !(group && group.isNote),
          }
        })
        .filter(comment => comment.value)

      // The AI only reads subject/description/comments, and Groove's per-event
      // author names arrive as "Unknown", so surface the contact explicitly by
      // prepending it to the description (name + email when available).
      const contactName = (contact && contact.name) || ''
      const contactEmail = (contact && contact.email) || ''
      const contactLine =
        contactName || contactEmail
          ? `Customer: ${[contactName, contactEmail && `<${contactEmail}>`]
              .filter(Boolean)
              .join(' ')}`
          : ''
      const firstMessage = (comments[0] && comments[0].value) || ''

      return {
        id: ticketId,
        subject: (conversation && conversation.subject) || '',
        description: [contactLine, firstMessage].filter(Boolean).join('\n\n'),
        comments,
      }
    },
    [ticketId, conversation, eventGroups, contact]
  )

  const postToIframe = useCallback(
    message => {
      const win = iframeRef.current && iframeRef.current.contentWindow
      if (win && helplyOrigin) {
        win.postMessage(message, helplyOrigin)
      }
    },
    [helplyOrigin]
  )

  const sendInit = useCallback(
    () => {
      if (!subdomain) return
      postToIframe({
        source: 'groove',
        type: 'init',
        payload: {
          subdomain,
          agentName,
          colorScheme: 'light',
        },
      })
    },
    [postToIframe, subdomain, agentName]
  )

  const sendContext = useCallback(
    () => {
      postToIframe({
        source: 'groove',
        type: 'context',
        payload: { ticket: ticketContext, agentName },
      })
    },
    [postToIframe, ticketContext, agentName]
  )

  const sendEditor = useCallback(
    () => {
      postToIframe({
        source: 'groove',
        type: 'editor',
        payload: { content: (activeDraft && activeDraft.body) || '' },
      })
    },
    [postToIframe, activeDraft]
  )

  const handleToggle = useCallback(() => setIsOpen(open => !open), [])

  // Mount the iframe on first open and keep it mounted thereafter.
  useEffect(
    () => {
      if (isOpen) setHasOpened(true)
    },
    [isOpen]
  )

  // Refocus the copilot input whenever the drawer is reopened. The iframe stays
  // mounted across open/close, so its input only autofocuses on the first open;
  // subsequent reopens need an explicit nudge.
  useEffect(
    () => {
      const reopened = isOpen && !wasOpenRef.current && hasOpened
      wasOpenRef.current = isOpen
      if (reopened) {
        postToIframe({ source: 'groove', type: 'focus' })
      }
    },
    [isOpen, hasOpened, postToIframe]
  )

  // When the copilot opens, open Groove's right sidebar (if collapsed) so the
  // conversation/editor reflows clear of the docked drawer instead of being
  // hidden behind it. We intentionally don't collapse it again on close —
  // leave the agent's sidebar as they had it.
  useEffect(
    () => {
      if (isOpen && isRightSidebarCollapsedRef.current) {
        dispatch(doToggleRightSidebar())
      }
    },
    [isOpen, dispatch]
  )

  // Handle messages coming back from the copilot iframe.
  useEffect(
    () => {
      // Don't listen until the addon is on AND we have a valid iframe origin to
      // validate messages against.
      if (!hasCopilotAddon || !helplyOrigin) return undefined

      const handleMessage = event => {
        // Only trust messages from our mounted iframe window at its exact
        // origin. If the iframe isn't mounted yet (no contentWindow) or either
        // check fails, drop the message rather than acting on a `source` tag
        // that any frame could forge.
        const win = iframeRef.current && iframeRef.current.contentWindow
        if (!win || event.source !== win || event.origin !== helplyOrigin) {
          return
        }

        const data = event.data
        if (
          !data ||
          typeof data !== 'object' ||
          data.source !== 'helply-copilot'
        ) {
          return
        }

        switch (data.type) {
          case 'ready':
            sendInit()
            sendContext()
            sendEditor()
            break
          case 'close':
            setIsOpen(false)
            break
          case 'request-grant':
            // The iframe needs an account-scoped grant to authenticate. Mint
            // one via Groove's backend and hand it back.
            dispatch(doFetchHelplyCopilotGrant()).then(grant => {
              postToIframe({
                source: 'groove',
                type: 'grant',
                payload: { grant: grant || null },
              })
            })
            break
          case 'insert':
            dispatch(
              doInsertHelplyCopilotText({
                html: data.payload && data.payload.html,
                asNote: !!(data.payload && data.payload.asNote),
              })
            )
            break
          case 'set-editor':
            dispatch(
              doSetHelplyCopilotEditor({
                html: data.payload && data.payload.html,
              })
            )
            break
          default:
            break
        }
      }

      window.addEventListener('message', handleMessage)
      return () => window.removeEventListener('message', handleMessage)
    },
    [
      hasCopilotAddon,
      helplyOrigin,
      sendInit,
      sendContext,
      sendEditor,
      postToIframe,
      dispatch,
    ]
  )

  // Keep the iframe in sync as the agent switches conversations / edits the draft.
  useEffect(
    () => {
      if (isOpen) sendContext()
    },
    [isOpen, sendContext]
  )

  useEffect(
    () => {
      if (isOpen) sendEditor()
    },
    [isOpen, sendEditor]
  )

  // Stable, account-level requirements — without these there's no copilot at
  // all for this account/agent. (`agentName` is sent to the copilot and is the
  // per-agent rate-limit key on the account-scoped Groove session.)
  if (!config.helplyCopilotUrl || !hasCopilotAddon || !agentName) return null

  // Per-ticket readiness (a loaded ticket) gates only the FIRST open — so the AI
  // isn't asked against a half-loaded conversation. Once opened, we keep the
  // panel + iframe mounted even when `ticketId` momentarily clears: in classic
  // view, switching tickets routes through the ticket list (no ticket), and
  // unmounting the iframe here would reload it and force a re-auth.
  if (!hasOpened && (!ticketId || !isTicketLoaded)) return null

  const iframeSrc = `${config.helplyCopilotUrl.replace(
    /\/$/,
    ''
  )}/copilot/groove/index.html`

  return (
    <>
      {/* No ticket (e.g. the classic-view list between tickets): hide the
          launcher, but keep the iframe mounted below so it doesn't re-auth. */}
      {ticketId && (
        <button
          type="button"
          onClick={handleToggle}
          aria-label="Toggle Helply Copilot"
          css={styles.toggleButton}
        >
          <ThreeStars />
        </button>
      )}
      <div css={styles.drawer(isOpen && !!ticketId)}>
        {(isOpen || hasOpened) && (
          <iframe
            ref={iframeRef}
            title="Helply Copilot"
            src={iframeSrc}
            css={styles.iframe}
            allow="clipboard-write"
          />
        )}
      </div>
    </>
  )
}
